import React, { useState, useEffect, useCallback } from 'react';
import { X, Star } from '@phosphor-icons/react';
import { useRuntime } from '../context/RuntimeContext';

const STORAGE_DISMISSED = 'ai_optimizer_review_dismissed';
const STORAGE_SESSIONS  = 'aio_review_sessions';
const STORAGE_PAGES     = 'aio_review_pages';
const SESSION_FLAG      = 'aio_review_session_counted';
const COOLDOWN_DAYS     = 7;
const MIN_SESSIONS      = 3;
const MIN_PAGES         = 5;

function isCurrentlyDismissed() {
  const val = localStorage.getItem(STORAGE_DISMISSED);
  if (!val) return false;
  if (val === '1') return true;
  const ts = new Date(val).getTime();
  if (isNaN(ts)) return false;
  return Date.now() - ts < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
}

function getCount(key) {
  return parseInt(localStorage.getItem(key) || '0', 10);
}

export const ReviewPopup = () => {
  const { t } = useRuntime();
  const [visible,  setVisible]  = useState(false);
  const [hovered,  setHovered]  = useState(0);
  const [selected, setSelected] = useState(0);
  const [phase,    setPhase]    = useState('rating');

  const show = useCallback(() => {
    if (!isCurrentlyDismissed()) setVisible(true);
  }, []);

  useEffect(() => {
    if (isCurrentlyDismissed()) return;

    let timer = null;

    if (!sessionStorage.getItem(SESSION_FLAG)) {
      sessionStorage.setItem(SESSION_FLAG, '1');
      const sessions = getCount(STORAGE_SESSIONS) + 1;
      localStorage.setItem(STORAGE_SESSIONS, sessions);
      if (sessions >= MIN_SESSIONS) {
        timer = setTimeout(show, 8000);
      }
    }

    if (!timer && getCount(STORAGE_PAGES) >= MIN_PAGES) {
      timer = setTimeout(show, 3000);
    }

    const onPageVisit = () => {
      const pages = getCount(STORAGE_PAGES) + 1;
      localStorage.setItem(STORAGE_PAGES, pages);
      if (pages >= MIN_PAGES && !isCurrentlyDismissed()) show();
    };

    const onTrigger = () => {
      if (!isCurrentlyDismissed()) show();
    };

    window.addEventListener('aio:page-visit', onPageVisit);
    window.addEventListener('aio:review-trigger', onTrigger);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('aio:page-visit', onPageVisit);
      window.removeEventListener('aio:review-trigger', onTrigger);
    };
  }, [show]);

  const dismissTemporary = () => {
    localStorage.setItem(STORAGE_DISMISSED, new Date().toISOString());
    setVisible(false);
  };

  const dismissPermanent = () => {
    localStorage.setItem(STORAGE_DISMISSED, '1');
    setVisible(false);
  };

  const handleStar = (n) => {
    setSelected(n);
    setPhase(n >= 4 ? 'positive' : 'negative');
  };

  if (!visible) return null;

  const display = hovered || selected;
  const starLabels = [
    t('review_popup.star.terrible', 'Terrible'),
    t('review_popup.star.bad', 'Not good'),
    t('review_popup.star.average', 'Average'),
    t('review_popup.star.good', 'Good!'),
    t('review_popup.star.excellent', 'Excellent! 🚀'),
  ];
  const starHint = hovered ? starLabels[hovered - 1] : t('review_popup.rating_tap', 'Tap a star to rate');

  return (
    <div
      className="fixed bottom-6 right-6 z-[100]"
      style={{ animation: 'reviewSlideUp 0.4s cubic-bezier(.21,1.02,.73,1) both' }}
    >
      <style>{`
        @keyframes reviewSlideUp {
          from { opacity: 0; transform: translateY(32px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div className="relative w-[400px] max-w-[calc(100vw-24px)] bg-white border border-gray-200 shadow-2xl shadow-black/20 overflow-hidden">

        {/* Header gradient */}
        <div className="relative bg-gradient-to-br from-[#00196b] via-[#002FA7] to-[#1e55d8] px-6 pt-5 pb-9 overflow-hidden">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -left-5 bottom-0 w-24 h-24 rounded-full bg-white/10" />
          <div className="absolute right-10 bottom-2 w-12 h-12 rounded-full bg-white/5" />

          <button
            onClick={dismissTemporary}
            className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 transition-colors"
          >
            <X size={13} className="text-white" />
          </button>

          <div className="relative flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <div className="w-[64px] h-[64px] rounded-2xl bg-white p-1.5 shadow-lg overflow-hidden">
                <img src="/logo.png" alt="AI Optimizer" className="w-full h-full object-contain" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-400 border-2 border-white rounded-full" />
            </div>
            <div>
              <p className="text-white font-bold text-base leading-tight">
                {t('review_popup.greeting', "Hi! I'm here 👋")}
              </p>
              <p className="text-blue-200 text-xs mt-1 leading-snug">
                {t('review_popup.subtitle', 'Hope everything is going great with AI Optimizer!')}
              </p>
            </div>
          </div>
        </div>

        {/* Curved edge */}
        <div
          className="h-4 bg-gradient-to-br from-[#00196b] via-[#002FA7] to-[#1e55d8]"
          style={{ marginTop: -1 }}
        >
          <div className="h-4 bg-white" style={{ borderRadius: '12px 12px 0 0' }} />
        </div>

        {/* Body */}
        <div className="px-6 pt-1 pb-5">
          {phase === 'rating' && (
            <>
              <p className="text-sm font-bold text-gray-900 mb-1.5">
                {t('review_popup.rating_title', 'Are you enjoying the app?')}
              </p>
              <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                {t('review_popup.rating_desc', 'Your feedback helps us improve and reach more Shopify merchants.')}
              </p>

              <div className="flex gap-2 justify-center mb-2">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onMouseEnter={() => setHovered(n)}
                    onMouseLeave={() => setHovered(0)}
                    onClick={() => handleStar(n)}
                    className="focus:outline-none transition-transform hover:scale-110 active:scale-95"
                  >
                    <Star
                      size={42}
                      weight={display >= n ? 'fill' : 'regular'}
                      className={`transition-colors duration-100 ${display >= n ? 'text-amber-400' : 'text-gray-200'}`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-xs text-center text-gray-400 h-5 transition-all">{starHint}</p>
            </>
          )}

          {phase === 'positive' && (
            <>
              <div className="flex gap-2 justify-center mb-4 mt-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} size={28} weight="fill" className={n <= selected ? 'text-amber-400' : 'text-gray-200'} />
                ))}
              </div>
              <p className="text-base font-bold text-gray-900 text-center mb-2">
                {t('review_popup.positive_title', 'Fantastic! Thank you so much 🎉')}
              </p>
              <p className="text-sm text-gray-600 text-center mb-5 leading-relaxed">
                {t('review_popup.positive_desc', 'A review on the Shopify App Store makes a huge difference for us. It only takes 1 minute!')}
              </p>
              <a
                href="https://apps.shopify.com/ai-optimizer-seo"
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismissPermanent}
                className="btn-ai w-full justify-center text-sm"
              >
                {t('review_popup.review_btn', '⭐ Leave a review')}
              </a>
            </>
          )}

          {phase === 'negative' && (
            <>
              <div className="flex gap-2 justify-center mb-4 mt-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} size={28} weight="fill" className={n <= selected ? 'text-amber-400' : 'text-gray-200'} />
                ))}
              </div>
              <p className="text-base font-bold text-gray-900 text-center mb-2">
                {t('review_popup.negative_title', 'Thank you for the feedback 🙏')}
              </p>
              <p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">
                {t('review_popup.negative_desc', "We're working to improve every day. Do you have ideas on what we could do better?")}
              </p>
              <button
                onClick={dismissTemporary}
                className="btn-secondary w-full justify-center text-sm"
              >
                {t('review_popup.close_btn', 'Close')}
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-4 flex justify-between items-center border-t border-gray-100 pt-3">
          <button
            onClick={dismissPermanent}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            {t('review_popup.dismiss', "Don't show again")}
          </button>
          {phase === 'rating' && (
            <button
              onClick={dismissTemporary}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              {t('review_popup.skip', 'Skip →')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewPopup;
